import React, {useEffect, useRef, useState} from 'react';
import Translate, {t} from "../../../../../../../../../components/Translate";
import Input from "../../../../../../../../../components-new/form/Input";
import Switch from "../../../../../../../../../components-new/form/Switch";
import SilverButton from "../../../../../../../../../components-new/buttons/SilverButton";
import BlueButton from "../../../../../../../../../components-new/buttons/BlueButton";
import DeleteButton from "../../../../../../../../../components-new/buttons/actions/DeleteButton";
import DownloadIcon from "../../../../../../../../../assets/icons/DownloadIcon";
import Tooltip from "../../../../../../../../../components-new/general/Tooltip";
import GlobeIcon from "../../../../../../../../../assets/icons/GlobeIcon";
import PaperClipIcon from "../../../../../../../../../assets/icons/PaperClipIcon";
import PlusIcon from "../../../../../../../../../assets/icons/PlusIcon";
import RefreshIcon from "../../../../../../../../../assets/icons/RefreshIcon";
import Colors from "../../../../../../../../../utils/Colors";
import config from "../../../../../../../../../config";
import {
    addKnowledgeFile,
    addKnowledgeSite,
    deleteKnowledgeSource,
    getKnowledgeSources,
    refreshKnowledgeSource,
    updateKnowledgeSource
} from "../../../../../../../../../services/company/api";
import AiBlock from "./AiBlock";

const STATUS_TONE = {
    SUCCESS: 'ok',
    PARTIAL: 'warn',
    FAILED: 'error',
    RUNNING: 'busy',
    NEVER_RUN: 'idle',
};

const POLL_INTERVAL_MS = 4000;

const ACCEPTED = '.pdf,.doc,.docx,.txt,.md,.csv,.log';

const errorKeyOf = (error) =>
    error && error.response && typeof error.response.data === 'string'
        ? error.response.data
        : "somethingWentWrong";


// the backend sends a translation key, optionally "key:param1:param2", so the
// panel can show these in the operator's language instead of raw English
const StatusDetail = ({detail, user}) => {
    if (!detail) return null;

    const [key, ...params] = String(detail).split(':');
    let text = t({text: key, user});

    // an unknown key falls back to whatever the backend sent
    if (!text) return <span className="ai-source__meta">· {detail}</span>;

    params.forEach((value, index) => {
        text = text.replace('__PARAM' + (index + 1) + '__', value);
    });

    return (
        <Tooltip text={text} needTranslate={false} asChild>
            <span className="ai-source__meta">· {text}</span>
        </Tooltip>
    );
};

const AiKnowledgeSources = ({client, aiType, user, onKnowledgeChanged = () => {}, addNotification}) => {

    // Team AI keeps its own flow, so uploaded sources do not apply there
    const isSupported = aiType === config.AITypes.GPT_AI || aiType === config.AITypes.GEMINI_AI;

    const [sources, setSources] = useState([]);
    const [newUrl, setNewUrl] = useState('');
    const [busyId, setBusyId] = useState(null);
    const [isAdding, setIsAdding] = useState(false);

    const fileInputRef = useRef(null);

    useEffect(() => {
        let active = true;

        if (!isSupported) return;

        getKnowledgeSources(client.id)
            .then(data => {
                if (active) setSources(data || []);
            })
            .catch(() => {
            });

        return () => {
            active = false;
        };
    }, [client.id, isSupported]);

    // crawling now runs in the background, so poll while anything is still being
    // read and let each row settle on its final status by itself
    const hasRunning = sources.some(s => s.status === 'RUNNING');

    useEffect(() => {
        if (!hasRunning) return undefined;

        let active = true;
        const timer = setInterval(() => {
            getKnowledgeSources(client.id)
                .then(data => {
                    if (!active) return;

                    const next = data || [];
                    // a crawl or an upload has just finished writing its text, so
                    // the counters in the block below are out of date
                    if (!next.some(s => s.status === 'RUNNING')) onKnowledgeChanged();
                    setSources(next);
                })
                .catch(() => {
                });
        }, POLL_INTERVAL_MS);

        return () => {
            active = false;
            clearInterval(timer);
        };
    }, [hasRunning, client.id]);

    const replaceSource = (updated) =>
        setSources(prev => prev.map(s => (s.id === updated.id ? updated : s)));

    const handleAddSite = () => {
        if (!newUrl.trim()) return;
        setIsAdding(true);

        addKnowledgeSite(client.id, newUrl.trim())
            .then(created => {
                setSources(prev => prev.concat(created));
                onKnowledgeChanged();
                setNewUrl('');
                addNotification("successful", config.notificationLevels.success);
            })
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error))
            .finally(() => setIsAdding(false));
    };

    const handleFilePicked = (event) => {
        const file = event.target.files && event.target.files[0];
        // reset immediately so picking the same file twice still fires onChange
        event.target.value = '';
        if (!file) return;

        setIsAdding(true);

        addKnowledgeFile(client.id, file)
            .then(created => {
                setSources(prev => prev.concat(created));
                onKnowledgeChanged();
                if (created.status === 'FAILED') {
                    addNotification("knowledgeFileUnreadable", config.notificationLevels.error);
                } else {
                    addNotification("successful", config.notificationLevels.success);
                }
            })
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error))
            .finally(() => setIsAdding(false));
    };

    // returns as soon as the crawl is queued; the polling effect above takes it
    // from "reading" to the final status
    const handleRefresh = (source) => {
        setBusyId(source.id);

        refreshKnowledgeSource(client.id, source.id)
            .then(replaceSource)
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error))
            .finally(() => setBusyId(null));
    };

    const handleToggle = (source, enabled) => {
        updateKnowledgeSource(client.id, source.id, {enabled})
            .then(replaceSource)
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error));
    };

    const handleDelete = (source) => {
        deleteKnowledgeSource(client.id, source.id)
            .then(() => {
                setSources(prev => prev.filter(s => s.id !== source.id));
                onKnowledgeChanged();
                addNotification("deleted", config.notificationLevels.info);
            })
            .catch(error => addNotification(errorKeyOf(error), config.notificationLevels.error));
    };

    // the guard sits after the hooks on purpose: returning earlier would change the
    // number of hooks between renders, and React fails the whole screen when the
    // company switches its AI type in the selector above
    if (!isSupported) return null;

    return (
        <AiBlock titleKey="aiKnowledgeSources" hintKey="aiKnowledgeSourcesHint">
            <div className="ai-sources">

                {sources.length === 0 && (
                    <div className="ai-sources__empty"><Translate text="aiNoKnowledgeSources"/></div>
                )}

                {sources.map(source => {
                    const isSite = source.type === 'SITE';
                    const isBusy = busyId === source.id || source.status === 'RUNNING';

                    return (
                        <div className="ai-source" key={source.id}>
                            <span className="ai-source__icon">
                                {isSite
                                    ? <GlobeIcon width={16} height={16} style={{color: Colors.blue}} stroke="currentColor"/>
                                    : <PaperClipIcon width={16} height={16} style={{color: Colors.blue}} stroke="currentColor"/>}
                            </span>

                            <div className="ai-source__main">
                                <Tooltip text={source.name} needTranslate={false} asChild>
                                    <span className="ai-source__name">{source.name}</span>
                                </Tooltip>

                                <span className={'ai-source__status ai-source__status--' + (STATUS_TONE[source.status] || 'idle')}>
                                    <span className="ai-source__dot"/>
                                    <Translate text={(isSite ? 'siteCrawlStatus.' : 'fileReadStatus.') + source.status}/>
                                    {source.documentsStored > 0 && (
                                        <span className="ai-source__meta">
                                            · {source.documentsStored} {isSite
                                                ? <Translate text="pagesStored"/>
                                                : <Translate text="documentsStored"/>}
                                        </span>
                                    )}
                                    <StatusDetail detail={source.statusDetail} user={user}/>
                                </span>
                            </div>

                            <div className="ai-source__actions">
                                <Tooltip text="autoUpdate" inRightSide style={{display: 'flex'}}>
                                    <Switch
                                        checked={source.enabled}
                                        onChange={(value) => handleToggle(source, value)}
                                        disabled={isBusy}
                                    />
                                </Tooltip>

                                {isSite && (
                                    <SilverButton
                                        width={'36px'}
                                        height={'36px'}
                                        styles={{padding: 0}}
                                        disabled={isBusy}
                                        onClick={() => handleRefresh(source)}
                                    >
                                        <RefreshIcon width={16} height={16} style={{color: Colors.blueDark}} stroke="currentColor"/>
                                    </SilverButton>
                                )}

                                {/*
                                    Only for a file we still hold a copy of. Files
                                    uploaded before the copy was kept have no url,
                                    and neither has one the file server refused, so
                                    the button appears per source rather than per
                                    type - the alternative is a download that leads
                                    to a 403.
                                */}
                                {!isSite && source.fileUrl && (
                                    <Tooltip text="download" inRightSide style={{display: 'flex'}}>
                                        <a
                                            className="ai-source__download"
                                            href={source.fileUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                        >
                                            <SilverButton
                                                asDiv
                                                width={'36px'}
                                                height={'36px'}
                                                styles={{padding: 0}}
                                            >
                                                <DownloadIcon width={16} height={16} style={{color: Colors.blueDark}} stroke="currentColor"/>
                                            </SilverButton>
                                        </a>
                                    </Tooltip>
                                )}

                                <DeleteButton onClick={() => handleDelete(source)}/>
                            </div>
                        </div>
                    );
                })}

                <div className="ai-sources__add">
                    <Input
                        style={{flex: '1 1 240px'}}
                        placeholder={t({text: 'aiSiteUrlPlaceholder', user})}
                        value={newUrl}
                        onChange={(e) => setNewUrl(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === 'Enter') handleAddSite();
                        }}
                        disabled={isAdding}
                    />

                    <BlueButton
                        width={'auto'}
                        height={'40px'}
                        // BlueButton has no flex layout of its own, so the icon
                        // would sit on the text baseline instead of the centre
                        styles={{
                            minWidth: '150px',
                            padding: '0 20px',
                            whiteSpace: 'nowrap',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px'
                        }}
                        disabled={!newUrl.trim() || isAdding}
                        onClick={handleAddSite}
                    >
                        <PlusIcon width={16} height={16} stroke={'#fff'}/>
                        <Translate text="aiAddSite"/>
                    </BlueButton>

                    <label className="ai-sources__upload">
                        <input
                            ref={fileInputRef}
                            type="file"
                            accept={ACCEPTED}
                            onChange={handleFilePicked}
                            disabled={isAdding}
                        />
                        <SilverButton
                            asDiv
                            width={'auto'}
                            height={'40px'}
                            styles={{padding: '0 20px', minWidth: '170px', whiteSpace: 'nowrap'}}
                        >
                            <PaperClipIcon width={16} height={16} style={{color: Colors.blueDark}} stroke="currentColor"/>
                            <Translate text="aiUploadFile"/>
                        </SilverButton>
                    </label>
                </div>

                <div className="ai-sources__formats"><Translate text="aiSupportedFileTypes"/></div>
            </div>
        </AiBlock>
    );
};

export default AiKnowledgeSources;



// WEBPACK FOOTER //
// ./src/scenes/dashboard/components/management/clients/new/configurations/components/AI/AiKnowledgeSources.jsx